import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { OrganizationRoles } from 'src/enums'
import { homeUrl, onboardingCardUrl } from 'src/navigation/urls'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

type OnboardingDropoffStatus = {
  /** Flag on AND user role can register a source card. */
  eligible: boolean
  /**
   * True while we're still evaluating *or* we've decided to redirect but
   * haven't navigated yet. Use this to suppress page chrome and homepage
   * content so the user doesn't see a flash before the redirect fires.
   *
   * Specifically: eligible AND (statistics loading OR data not yet available
   * OR source-card count is zero).
   */
  isResumingOnboarding: boolean
  /** True only once data has loaded and shows zero source cards. */
  shouldRedirect: boolean
}

/**
 * Read-only check for the onboarding drop-off resume state. Safe to call
 * from anywhere in the authed tree — the `/statistics` request is deduped
 * by React Query so multiple callers share a single network roundtrip.
 *
 * The actual redirect lives in `useOnboardingDropoffRedirect` and must be
 * called from exactly one place (`Home.tsx`) so the one-shot ref guard
 * works.
 */
export function useOnboardingDropoffStatus(): OnboardingDropoffStatus {
  const location = useLocation()
  const user = useSelector(selectUserListModelFromState)
  const enableOnboardingResumeOnDropoff = useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_onboarding_resume_on_dropoff

  // Only org owners go through the original onboarding flow, so they're the
  // only role that can have "dropped off." Admins and card managers are added
  // to orgs that have already been set up — they should never see this
  // redirect, regardless of org card count.
  const isCardRegistrationEligibleRole =
    user?.organizationRole === OrganizationRoles.owner
  // Gate the entire flow to the homepage route: this redirect only applies
  // when the user lands on /home, so we must not fire /statistics nor
  // suppress chrome on any other authed page.
  const onHomepage = location.pathname === homeUrl()
  const eligible =
    !!enableOnboardingResumeOnDropoff &&
    isCardRegistrationEligibleRole &&
    onHomepage

  // Use the org-wide source-card count, not the viewer-scoped one. A promoted
  // owner may personally have zero cards while the org already has cards
  // registered by a previous owner — we don't want to push them back through
  // onboarding in that case.
  const { results, anyLoading } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.CreditCardsOrgSourceCount],
    enabled: eligible,
  })
  const sourceCardResult = results?.CreditCardsOrgSourceCount
  const sourceCardCount = sourceCardResult?.data
  // React Query's `isLoading` is false whenever cached data exists, even while
  // a fresh refetch is in flight. `isFetching` covers the cached-but-pending
  // window — without this, a stale cached zero can synchronously fire the
  // redirect on a subsequent /home visit before the fresh /statistics result
  // arrives.
  const isFetching = !!sourceCardResult?.isFetching

  // Treat an error / empty response after loading as "not resuming" — don't
  // strand the user on a loading state or suppress chrome indefinitely.
  // Only the explicit zero-count case is a definite resume, and only when a
  // fresh result is in hand (not cached data with a pending refetch).
  const shouldRedirect =
    eligible && !anyLoading && !isFetching && sourceCardCount === 0
  const isResumingOnboarding =
    eligible && (anyLoading || isFetching || sourceCardCount === 0)

  return { eligible, isResumingOnboarding, shouldRedirect }
}

/**
 * EX-39432 — when the `enable_onboarding_resume_on_dropoff` flag is on and an
 * org owner, admin, or card-manager lands with no source card on file,
 * redirect them to the card-registration step of onboarding so they can
 * finish setup.
 *
 * Fires once per mount (guarded by a ref), and only after the statistics call
 * has resolved. No-op when the flag is off or the user isn't eligible — the
 * statistics request is gated to skip in those cases.
 *
 * Returns the same `OnboardingDropoffStatus` shape as
 * `useOnboardingDropoffStatus` so the caller can suppress UI while the
 * decision is pending. Call this from exactly one place (the homepage) so
 * the one-shot ref guard works.
 */
export function useOnboardingDropoffRedirect(): OnboardingDropoffStatus {
  const history = useHistory()
  const { analytics } = useAnalyticsContext()
  const status = useOnboardingDropoffStatus()
  const { shouldRedirect } = status

  const hasRedirectedRef = useRef(false)
  useEffect(() => {
    if (hasRedirectedRef.current || !shouldRedirect) {
      return
    }
    hasRedirectedRef.current = true
    analytics.track('onboarding drop-off resume', { action: 'redirected' })
    history.replace(onboardingCardUrl())
  }, [shouldRedirect, analytics, history])

  return status
}
